'use client';
import React, { useEffect, useState } from 'react';
import { List, ListSubheader, Box, Divider, Stack, Typography, Button, ListItem } from '@unity/cloud-ui';
import { ArrowUp } from '@phosphor-icons/react/dist/ssr';
import { useTranslations } from 'next-intl';
import themeConstants from '@/components/theme/theme-constants';
import { scrollToAnchor } from '@/utils/scroll-to-anchor';
import { FeedbackForm } from '../feedback-form';
import { AdditionalResourcesItem } from './additional-resources-item';
import { TableOfContentListItem } from './table-of-contents-list-item';
import type { Toc } from '@stefanprobst/rehype-extract-toc';

export function TableOfContents(props: {
  tableOfContents: Toc;
  additionalResources?: AdditionalResourcesItem[];
  pageTitle: string;
  showFeedbackForm?: boolean;
}) {
  const t = useTranslations();
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  function handleBackToTop() {
    // Scroll to the top of the page and TOC
    [window, document.querySelector("[data-nav='table-of-contents']")].forEach((el) => {
      el?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  function handleItemClick(id: string) {
    setSelectedItemId(id);
  }

  useEffect(() => {
    const anchorName = window.location.hash.replace('#', '');
    if (anchorName) {
      scrollToAnchor(anchorName);
      setSelectedItemId(anchorName);
    }
  }, []);

  return (
    <Box
      sx={{
        position: 'fixed',
        display: 'flex',
        flexDirection: 'column',
        height: `calc(100svh - ${themeConstants.layout.headerHeight} - ${themeConstants.layout.productHeaderHeight})`,
        width: 'inherit',
        maxWidth: themeConstants.layout.tocMaxWidth,
        pr: 2,
        py: 4,
        overflow: 'hidden',
      }}
    >
      <Box
        component="nav"
        data-nav="table-of-contents"
        aria-label={t('accessibility:table-of-contents')}
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          maskImage: `linear-gradient(to top, rgba(0, 0, 0, 0) 0, rgba(0, 0, 0, 1) ${theme.spacing(10)}, rgba(0, 0, 0, 1))`,
        })}
      >
        <List dense sx={{ pt: 0, pb: 6 }}>
          {props.tableOfContents.length > 0 && (
            <>
              <ListSubheader sx={{ p: 0, mb: 1, position: 'relative' }}>
                <Typography noWrap color="text.secondary" variant="h6" component="div">
                  {t('navigation:on-this-page')}
                </Typography>
              </ListSubheader>
              <ListItem
                sx={{
                  '&:hover': {
                    backgroundColor: 'transparent !important',
                    cursor: 'default',
                  },
                }}
              >
                <List
                  disablePadding
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    width: 'inherit',
                  }}
                >
                  {props.tableOfContents.map((item, index) => (
                    <TableOfContentListItem
                      key={index}
                      item={item}
                      selectedItemId={selectedItemId}
                      handleItemClick={handleItemClick}
                    />
                  ))}
                </List>
              </ListItem>
            </>
          )}

          {props.additionalResources && props.additionalResources.length > 0 && (
            <>
              <ListSubheader sx={{ p: 0, mt: 2, mb: 1 }}>
                <Typography noWrap color="text.secondary" variant="h6" component="div">
                  {t('navigation:additional-resources')}
                </Typography>
              </ListSubheader>
              {props.additionalResources.map((item: AdditionalResourcesItem) => (
                <AdditionalResourcesItem key={item.title} item={item} />
              ))}
            </>
          )}
        </List>
      </Box>

      <Stack spacing={1.5} overflow="hidden">
        <Divider />
        {props.showFeedbackForm && <FeedbackForm pageTitle={props.pageTitle} />}
        <Button
          color="default"
          variant="text"
          startIcon={<ArrowUp />}
          onClick={handleBackToTop}
          sx={{
            py: 0.75,
            px: 1,
            ml: -2,
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1,
            '&:hover': {
              backgroundColor: 'default.main',
            },
          }}
          aria-label={t('navigation:back-to-top')}
        >
          <Typography>{t('navigation:back-to-top')}</Typography>
        </Button>
      </Stack>
    </Box>
  );
}
